import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { fetchAuthSession } from "aws-amplify/auth"
import { StripePaymentMethod } from "ipctg-inflow-types/InFlowAPIMisc"
import {
  GatedSavedPaymentMethodActionsFailReason,
  SavedPaymentMethodAction,
} from "ipctg-inflow-types/InFlowEnums"
import { useState } from "react"
import { GridList, GridListItem, MenuTrigger } from "react-aria-components"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import Menu from "@/globalComponents/molecules/Menu"
import { apiCardBrandToIconName } from "@/utilities/etc"
import { GatedSavedPaymentMethodActionError } from "@/utilities/expectedErrors"
import { savedPaymentMethodsOptions } from "@/utilities/hooks/queries/user"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"
import { useScopedTo } from "@/utilities/hooks/useScopedRouting"

import { DeletePaymentMethodModal } from "./-components/DeletePaymentMethodModal"
import styles from "./index.module.css"

export const Route = createFileRoute(
  "/_authenticated/account/saved-payment-methods/"
)({
  component: SavedPaymentMethodsRouteComponent,
  beforeLoad: ({ context }) => {
    context.queryClient.fetchQuery(savedPaymentMethodsOptions())
  },
})

export function SavedPaymentMethodsRouteComponent() {
  const { data, refetch } = useSuspenseQuery(savedPaymentMethodsOptions())
  const scoped = useScopedTo()
  const { t } = useTranslation(["common", "errors"])

  const addPaymentMethodLabel = (
    <>
      <Icon name="add" slot="left" />
      {t("account.savedPaymentMethods.addNewPaymentMethod")}
    </>
  )
  const { setError } = useErrorModal()
  const [paymentMethodToDelete, setPaymentMethodToDelete] =
    useState<StripePaymentMethod | null>(null)

  const { mutate, mutateAsync, isPending, variables } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <Card>
      <Heading level={2} id="saved-payment-methods-header">
        {t("account.savedPaymentMethods.header")}
      </Heading>
      <GridList
        items={data.paymentMethods.sort(
          (a, b) => Number(b.isDefault) - Number(a.isDefault)
        )}
        aria-labelledby="saved-payment-methods-header"
        className={styles.paymentMethodList}
        dependencies={[data, variables, isPending]}
        renderEmptyState={() => (
          <Text>{t("account.savedPaymentMethods.noSavedPaymentMethods")}</Text>
        )}
      >
        {(paymentMethod) => (
          <GridListItem
            id={paymentMethod.paymentMethodID}
            key={paymentMethod.paymentMethodID}
            textValue={`${paymentMethod.cardBrand} ${paymentMethod.last4}`}
          >
            <div className={styles.brand}>
              <Icon
                name={apiCardBrandToIconName(paymentMethod)}
                retainOriginalColors
              />
            </div>
            <div>
              <Text className="bold">•••• {paymentMethod.last4}</Text>
              {paymentMethod.isDefault && (
                <Text level={3}>
                  {t("account.savedPaymentMethods.defaultPaymentMethod")}
                </Text>
              )}
            </div>
            {/* TODO, make into a component */}
            <MenuTrigger>
              <Button
                presentation="ghost"
                buttonType="primary"
                isPending={
                  isPending &&
                  variables.paymentMethod.paymentMethodID ===
                    paymentMethod.paymentMethodID
                }
              >
                <Icon name="kebab" />
              </Button>
              <Menu>
                {!paymentMethod.isDefault && (
                  <Menu.Item
                    onAction={() =>
                      mutate({
                        paymentMethod,
                        action: SavedPaymentMethodAction.MAKE_DEFAULT,
                      })
                    }
                  >
                    <Icon name="creditCard" slot="left" />
                    {t("account.savedPaymentMethods.menuOptions.makeDefault")}
                  </Menu.Item>
                )}
                <Menu.Item
                  hue="danger"
                  onAction={() => setPaymentMethodToDelete(paymentMethod)}
                >
                  <Icon name="trash" slot="left" />
                  {t("account.savedPaymentMethods.menuOptions.delete")}
                </Menu.Item>
              </Menu>
            </MenuTrigger>
          </GridListItem>
        )}
      </GridList>
      <LinkButton
        buttonType="primary"
        {...scoped({ to: "/account/saved-payment-methods/add" })}
      >
        {addPaymentMethodLabel}
      </LinkButton>
      <DeletePaymentMethodModal
        isOpen={!!paymentMethodToDelete}
        onOpenChange={(open) => {
          if (!open) setPaymentMethodToDelete(null)
        }}
        isPending={isPending}
        onConfirmDelete={onConfirmDelete}
      />
    </Card>
  )

  async function onConfirmDelete() {
    if (!paymentMethodToDelete) return
    await mutateAsync({
      paymentMethod: paymentMethodToDelete,
      action: SavedPaymentMethodAction.DELETE,
    })
  }

  async function mutationFn(args: {
    paymentMethod: StripePaymentMethod
    action: SavedPaymentMethodAction
  }) {
    const session = await fetchAuthSession()
    const cognitoIDToken = session.tokens?.idToken?.toString()

    if (!cognitoIDToken) {
      throw new GatedSavedPaymentMethodActionError(
        GatedSavedPaymentMethodActionsFailReason.Unauthorized
      )
    }

    const rsp = await api.savedPaymentMethodActions.POST({
      cognitoIDToken,
      action: args.action,
      paymentMethodID: args.paymentMethod.paymentMethodID,
    })

    if ("failReason" in rsp) {
      throw new GatedSavedPaymentMethodActionError(rsp.failReason)
    }

    return rsp
  }

  // TODO need to make an optimistic update onMutation
  function onSuccess() {
    refetch()
  }

  function onError(error: Error) {
    setError({ error })
  }
}
